import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Landmark, Copy, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ImpactMeter from "../components/ImpactMeter";
import SharePreview from "../components/SharePreview";

export default function Pledge() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [makePublic, setMakePublic] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [config, setConfig] = useState(null);
  const [frequency, setFrequency] = useState("once");
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [refCode, setRefCode] = useState("");
  const [refInfo, setRefInfo] = useState(null); // {valid, ambassador_name}

  // Auto-load referral code from ?ref= or localStorage
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const q = (params.get("ref") || params.get("r") || "").toUpperCase();
    if (q) {
      setRefCode(q);
      localStorage.setItem("bdu_ref_code", q);
    } else {
      const saved = localStorage.getItem("bdu_ref_code");
      if (saved) setRefCode(saved);
    }
  }, []);

  // Live-validate the referral code as user types (debounced)
  useEffect(() => {
    if (!refCode) { setRefInfo(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/referrals/validate/${encodeURIComponent(refCode.toUpperCase())}`);
        setRefInfo(data);
      } catch { setRefInfo({ valid: false }); }
    }, 400);
    return () => clearTimeout(t);
  }, [refCode]);

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const copy = (val) => {
    navigator.clipboard.writeText(val);
    toast.success("Copied to clipboard");
  };

  const donateWithCard = async (e) => {
    e.preventDefault();
    if (!user || user === false) {
      navigate("/login?next=/pledge");
      return;
    }
    const value = parseFloat(amount);
    if (!value || value <= 0) {
      toast.error("Enter a valid donation amount");
      return;
    }
    setSubmitting(true);
    setCheckoutUrl(null);
    try {
      const { data } = await api.post("/payments/pledge-checkout", {
        amount_dollars: value,
        origin_url: window.location.origin,
        email: user?.email,
        note,
        public: makePublic,
        frequency,
        referral_code: refCode || undefined,
      });
      // Save URL so mobile users can tap manually if auto-redirect is blocked
      setCheckoutUrl(data.checkout_url);
      // Attempt auto-redirect (works on desktop and most mobile browsers)
      window.location.assign(data.checkout_url);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start checkout");
      setSubmitting(false);
    }
  };

  const recordCommitment = async () => {
    if (!user || user === false) {
      navigate("/login?next=/pledge");
      return;
    }
    const value = parseFloat(amount);
    if (!value || value <= 0) {
      toast.error("Enter a valid pledge amount");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/pledges", { amount: value, note, public: makePublic, referral_code: refCode || undefined });
      toast.success(`Pledge of $${value.toLocaleString()} recorded. Deposit funds at Citizen Trust Bank when ready.`);
      setAmount("");
      setNote("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const presets = [
    { amount: 1, tag: null },
    { amount: 5, tag: null },
    { amount: 10, tag: null },
    { amount: 25, tag: null },
    { amount: 100, tag: "Popular" },
    { amount: 500, tag: null },
    { amount: 1000, tag: null },
    { amount: 5000, tag: null },
    { amount: 10000, tag: null },
  ];

  return (
    <div className="grain min-h-screen">
      <section className="max-w-7xl mx-auto px-6 sm:px-10 pt-20 pb-16" data-testid="pledge-hero">
        <div className="label-eyebrow">Pledge & Deposit</div>
        <h1 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl text-bdu-cream tracking-tight max-w-4xl">
          Every dollar you pledge is a <span className="italic text-bdu-gold">brick</span> in a Black-owned economy.
        </h1>
        <p className="mt-6 text-bdu-cream/70 max-w-2xl leading-relaxed">
          Record your pledge here so it counts toward the $50M goal. Then deposit funds directly at
          Citizen Trust Bank — a Black-owned financial institution — into the Black Dollars United fund account.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-24 grid lg:grid-cols-2 gap-8">
        {/* Bank Deposit Info */}
        <div className="bdu-card p-8 lg:p-10" data-testid="bank-info-card">
          <div className="flex items-center gap-3">
            <Landmark size={28} strokeWidth={1.25} className="text-bdu-gold" />
            <div>
              <div className="label-eyebrow">Depository Bank</div>
              <div className="font-serif text-2xl text-bdu-cream mt-1">{config?.bank_name || "Citizen Trust Bank"}</div>
            </div>
          </div>
          <p className="mt-6 text-sm text-bdu-cream/70 leading-relaxed">
            All fund contributions are held at Citizen Trust Bank, one of the oldest Black-owned depository
            institutions in the United States (est. 1921). Make your deposit in-branch, by wire, or by ACH.
          </p>

          <div className="mt-8 divider-gold" />

          <dl className="mt-8 space-y-5">
            <BankRow label="Account Name" value={config?.bank_account_name || "Black Dollar United Fund"} onCopy={copy} />
            <BankRow label="Routing Number" value={config?.bank_routing_number || "—"} onCopy={copy} />
            <BankRow label="Account Number" value={config?.bank_account_number || "—"} onCopy={copy} />
            <BankRow label="Memo Line" value="Trillion Dollar Spending Power Fund" onCopy={copy} />
          </dl>

          <div className="mt-8 flex items-start gap-3 p-4 border border-bdu-gold/25 bg-bdu-bg/40" data-testid="bank-security-notice">
            <ShieldCheck size={18} className="text-bdu-gold shrink-0 mt-0.5" />
            <p className="text-xs text-bdu-cream/75 leading-relaxed">
              <strong className="text-bdu-gold">Deposit-only account.</strong> The TDSP Fund account at Citizen Trust Bank
              is protected by <strong>ACH Debit Block</strong> and <strong>Positive Pay</strong> — inbound deposits only.
              No withdrawals can be initiated from published account details.
              Stripe payouts and donor deposits flow in; only signed BDU officers can move funds out.
            </p>
          </div>
        </div>

        {/* Pledge Form */}
        <div className="bdu-card p-8 lg:p-10" data-testid="pledge-form-card">
          <div className="label-eyebrow">Donate to the Fund</div>
          <h2 className="mt-4 font-serif text-3xl text-bdu-cream">Give in one click. Or pledge for later.</h2>
          <p className="mt-3 text-sm text-bdu-cream/60">
            Pay by card now (money flows to Citizen Trust Bank via Stripe), or record a pledge and deposit at the bank on your own timeline.
          </p>

          <form onSubmit={donateWithCard} className="mt-8 space-y-6">
            {/* Frequency toggle */}
            <div>
              <div className="label-eyebrow mb-3">Give</div>
              <div className="grid grid-cols-2 gap-2" data-testid="pledge-frequency-toggle">
                <button
                  type="button"
                  onClick={() => setFrequency("once")}
                  data-testid="freq-once-btn"
                  className={`px-4 py-3 border text-sm uppercase tracking-widest transition-colors ${
                    frequency === "once"
                      ? "border-bdu-gold bg-bdu-gold text-bdu-black font-semibold"
                      : "border-bdu-gold/20 text-bdu-cream/70 hover:border-bdu-gold hover:text-bdu-gold"
                  }`}
                >
                  One-Time
                </button>
                <button
                  type="button"
                  onClick={() => setFrequency("monthly")}
                  data-testid="freq-monthly-btn"
                  className={`relative px-4 py-3 border text-sm uppercase tracking-widest transition-colors ${
                    frequency === "monthly"
                      ? "border-bdu-gold bg-bdu-gold text-bdu-black font-semibold"
                      : "border-bdu-gold/20 text-bdu-cream/70 hover:border-bdu-gold hover:text-bdu-gold"
                  }`}
                >
                  Monthly
                  <span className={`absolute -top-2 -right-2 px-1.5 py-0.5 text-[9px] tracking-wider font-semibold rounded-sm ${
                    frequency === "monthly" ? "bg-bdu-black text-bdu-gold" : "bg-bdu-gold text-bdu-black"
                  }`}>
                    Impact
                  </span>
                </button>
              </div>
              {frequency === "monthly" && (
                <p className="mt-2 text-xs text-bdu-cream/60">
                  Sustained monthly giving is the fastest path to $50M. Cancel anytime from your Stripe receipt email.
                </p>
              )}
            </div>

            <div>
              <div className="label-eyebrow mb-3">Amount (USD)</div>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-bdu-gold font-serif text-2xl">$</span>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full bg-transparent border border-bdu-gold/25 pl-10 pr-4 py-4 text-bdu-cream text-2xl font-serif outline-none focus:border-bdu-gold"
                  placeholder="1,000"
                  data-testid="pledge-amount-input"
                />
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {presets.map((p) => (
                  <button
                    type="button"
                    key={p.amount}
                    onClick={() => setAmount(String(p.amount))}
                    className={`relative px-3 py-1.5 border text-xs uppercase tracking-widest transition-colors ${
                      p.tag
                        ? "border-bdu-gold text-bdu-gold hover:bg-bdu-gold/10"
                        : "border-bdu-gold/20 text-bdu-cream/80 hover:border-bdu-gold hover:text-bdu-gold"
                    }`}
                    data-testid={`pledge-preset-${p.amount}`}
                  >
                    ${p.amount.toLocaleString()}
                    {p.tag && (
                      <span className="absolute -top-2 -right-2 px-1.5 py-0.5 bg-bdu-gold text-bdu-black text-[9px] tracking-wider font-semibold rounded-sm">
                        {p.tag}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Impact Meter — visualizes what the selected amount unlocks */}
            <ImpactMeter amount={amount} />

            {/* Share-card preview — turns hesitation into desire */}
            <SharePreview amountDollars={amount} />

            {/* Referral Code — credit an Ambassador */}
            <div data-testid="pledge-referral-block">
              <div className="label-eyebrow mb-3">Ambassador Code <span className="text-bdu-cream/40 normal-case">· optional</span></div>
              <div className="relative">
                <input
                  type="text"
                  value={refCode}
                  onChange={(e) => setRefCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8))}
                  placeholder="e.g. A7NQPPEN"
                  className="bdu-input font-mono tracking-widest uppercase"
                  data-testid="pledge-referral-input"
                  maxLength={8}
                />
                {refCode && refInfo && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs">
                    {refInfo.valid ? (
                      <span className="text-emerald-400" data-testid="ref-valid">✓ {refInfo.ambassador_name}</span>
                    ) : (
                      <span className="text-red-400" data-testid="ref-invalid">Invalid code</span>
                    )}
                  </div>
                )}
              </div>
              <p className="mt-2 text-xs text-bdu-cream/50">
                Did an Ambassador refer you? Enter their code so their $ toward the fund is credited to them.
              </p>
            </div>

            <div>
              <div className="label-eyebrow mb-3">Message (optional)</div>
              <textarea
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="w-full bg-transparent border border-bdu-gold/25 px-4 py-3 text-bdu-cream outline-none focus:border-bdu-gold text-sm"
                placeholder="For the community that raised me..."
                data-testid="pledge-note-input"
              />
            </div>

            <label className="flex items-start gap-3 cursor-pointer group" data-testid="pledge-public-toggle-label">
              <input
                type="checkbox"
                checked={makePublic}
                onChange={(e) => setMakePublic(e.target.checked)}
                className="mt-1 w-4 h-4 accent-bdu-gold cursor-pointer"
                data-testid="pledge-public-toggle"
              />
              <span className="text-sm text-bdu-cream/75 leading-relaxed">
                <span className="text-bdu-cream group-hover:text-bdu-gold transition-colors">Add my name to the public Roll of Honor</span>
                <span className="block text-xs text-bdu-cream/50 mt-1">
                  Your name and pledge total appear on the landing page. Amount stays visible; personal info stays private.
                </span>
              </span>
            </label>

            {!user || user === false ? (
              <div className="p-4 border border-bdu-red/30 bg-bdu-red/5 text-sm text-bdu-cream/80">
                You must{" "}
                <Link to="/login?next=/pledge" className="text-bdu-gold underline">login</Link>{" "}
                or{" "}
                <Link to="/register?next=/pledge" className="text-bdu-gold underline">create an account</Link>{" "}
                to donate.
              </div>
            ) : null}

            <button type="submit" disabled={submitting} className="btn-gold w-full justify-center" data-testid="donate-card-btn">
              {submitting
                ? "Redirecting to secure checkout..."
                : `Donate ${frequency === "monthly" ? "Monthly" : "Now"}${amount ? ` — $${Number(amount).toLocaleString()}${frequency === "monthly" ? "/mo" : ""}` : ""}`}
            </button>
            {checkoutUrl && (
              <div className="mt-4 p-4 border-2 border-bdu-gold bg-bdu-gold/10 text-center" data-testid="checkout-fallback">
                <p className="text-sm text-bdu-cream mb-3">
                  If Stripe checkout didn't open automatically, tap the button below:
                </p>
                <a
                  href={checkoutUrl}
                  className="inline-block px-6 py-3 bg-bdu-gold text-bdu-black uppercase tracking-widest text-xs font-semibold hover:bg-bdu-gold/90"
                  data-testid="checkout-manual-link"
                >
                  Continue to Secure Checkout →
                </a>
              </div>
            )}
            <div className="text-xs text-center text-bdu-cream/50">
              Secured by Stripe · Funds routed to Citizen Trust Bank
            </div>

            <div className="relative flex items-center py-2">
              <div className="flex-grow border-t border-bdu-gold/15" />
              <span className="mx-4 text-xs text-bdu-cream/40 uppercase tracking-widest">or</span>
              <div className="flex-grow border-t border-bdu-gold/15" />
            </div>

            <button
              type="button"
              onClick={recordCommitment}
              disabled={submitting}
              className="w-full px-5 py-3 border border-bdu-gold/30 text-bdu-cream/85 hover:text-bdu-gold hover:border-bdu-gold text-sm uppercase tracking-widest transition"
              data-testid="record-pledge-btn"
            >
              Record Pledge Only (Deposit at Bank Later)
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

function BankRow({ label, value, onCopy }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div>
        <div className="label-eyebrow" style={{ fontSize: "0.6rem" }}>{label}</div>
        <div className="mt-1 font-serif text-lg text-bdu-cream break-all">{value}</div>
      </div>
      <button
        type="button"
        onClick={() => onCopy(value)}
        className="p-2 border border-bdu-gold/20 hover:border-bdu-gold text-bdu-cream/70 hover:text-bdu-gold transition-colors"
        aria-label={`Copy ${label}`}
        data-testid={`copy-${label.toLowerCase().replace(/\s/g, "-")}`}
      >
        <Copy size={14} />
      </button>
    </div>
  );
}
